import { formatMoney, randomUUID } from '@betterment/core';

import { BillingPlanFlowValues, FeeConfigType, isExistingPlanWithClients } from './data';
import { FirmQuery } from './graphql/query.firm.generated';

import { BillingRate, PaymentType, PlatformFeePaidBy, SingleBillingRate } from '../../../graphql/schema';

export type BillingPlan = FirmQuery['firm']['billingPlans'][number];

export const formatAdvisoryFee = (billingRate: BillingPlan['billingRate']): string => {
  if (billingRate.__typename === 'BillingRateAssetBased') return `${billingRate.feeBps} bps`;
  if (billingRate.__typename === 'BillingRateFixed') return formatMoney(billingRate.feeCents);
  return 'Tiered';
};

export const formatBillFrom = (paymentType: PaymentType): string =>
  paymentType === PaymentType.CashReserveFirst ? 'Cash Reserve first' : 'Investments';

export const formatPlatformFeePaidBy = (platformFeeAllocation: BillingPlan['platformFeeAllocation']): string => {
  if (platformFeeAllocation.__typename === 'NotSharedPlatformFeeAllocation') {
    return platformFeeAllocation.paidBy === PlatformFeePaidBy.AdvisorOnly ? 'Your firm' : 'Client';
  }
  return 'Shared';
};

export const formatSharedPlatformFeeBreakdown = (
  platformFeeAllocation: Extract<BillingPlan['platformFeeAllocation'], { __typename: 'SharedPlatformFeeAllocation' }>,
  effectivePlatformFee: number
): string => {
  const actualClientPortion = Math.min(Number(platformFeeAllocation.clientPortion), effectivePlatformFee);
  const advisorPortion = effectivePlatformFee - actualClientPortion;
  return `Client ${actualClientPortion} bps, your firm ${advisorPortion} bps`;
};

const determineFeeConfigType = (billingRate: SingleBillingRate): FeeConfigType => {
  if (billingRate.__typename === 'BillingRateAssetBased') {
    return 'assetBasedConfiguration';
  }
  if (billingRate.__typename === 'BillingRateFixed') {
    return 'fixedConfiguration';
  }
  throw new Error('Unsupported billing rate type');
};

export const deconstructBillingRate = (billingRate: BillingRate) => {
  switch (billingRate.__typename) {
    case 'BillingRateFixed':
      return {
        fixedFeeAmount: formatMoney(billingRate.feeCents, 'raw'),
        assetBasedFeeRate: '',
        tiers: [
          {
            uuid: randomUUID(),
            firmFee: {
              feeConfigType: 'assetBasedConfiguration',
              feeValue: '',
            },
            tierEnd: '',
          },
          {
            uuid: randomUUID(),
            firmFee: {
              feeConfigType: 'assetBasedConfiguration',
              feeValue: '',
            },
            tierEnd: undefined,
          },
        ],
      };
    case 'BillingRateAssetBased':
      return {
        fixedFeeAmount: '',
        assetBasedFeeRate: `${billingRate.feeBps}`,
        tiers: [
          {
            uuid: randomUUID(),
            firmFee: {
              feeConfigType: 'assetBasedConfiguration',
              feeValue: '',
            },
            tierEnd: '',
          },
          {
            uuid: randomUUID(),
            firmFee: {
              feeConfigType: 'assetBasedConfiguration',
              feeValue: '',
            },
            tierEnd: undefined,
          },
        ],
      };
    case 'BillingRateTiered':
      return {
        fixedFeeAmount: '',
        assetBasedFeeRate: '',
        tiers: billingRate.tiers.map(tier => ({
          uuid: randomUUID(),
          firmFee: {
            feeConfigType: determineFeeConfigType(tier.firmFee),
            feeValue:
              tier.firmFee.__typename === 'BillingRateAssetBased'
                ? `${tier.firmFee.feeBps}`
                : formatMoney(tier.firmFee.feeCents, 'raw'),
          },
          tierEnd: tier.tierEnd ? formatMoney(tier.tierEnd, 'raw') : undefined,
        })),
        tieredFeeCalculationMethod: billingRate.calculationMethod,
      };
    default:
      throw new Error('Unsupported billing rate type');
  }
};

export const getFlowTitle = (flowValues: BillingPlanFlowValues, currentStep: number): string => {
  const totalSteps = isExistingPlanWithClients(flowValues) ? 3 : 2;
  if (flowValues.billingPlanId) {
    return `Edit billing plan | ${currentStep} of ${totalSteps}`;
  }
  return `Create billing plan | ${currentStep} of ${totalSteps}`;
};
